/**
 * ============================================================================
 * Iron Meridian - Replay Hook
 * ============================================================================
 * 
 * React hook for managing replay state and controls.
 * Loads turn snapshots and provides deterministic playback.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export function useReplay(gameId) {
  const [replayData, setReplayData] = useState(null);
  const [currentTurnIndex, setCurrentTurnIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1000); // ms per turn
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const intervalRef = useRef(null);

  // Load replay data from API
  const loadReplay = useCallback(async () => {
    if (!gameId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/game/history?gameId=${gameId}`);
      
      if (!response.ok) {
        throw new Error('Failed to load replay data');
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to load replay');
      }
      
      setReplayData(result.data);
      setCurrentTurnIndex(0);
      console.log(`[REPLAY] Loaded ${result.data.turns.length} turns`);
      
    } catch (err) {
      console.error('[REPLAY] Error loading replay:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  // Load on mount
  useEffect(() => {
    if (gameId) {
      loadReplay();
    }
  }, [gameId, loadReplay]);

  // Auto-play logic
  useEffect(() => {
    if (isPlaying && replayData) {
      intervalRef.current = setInterval(() => {
        setCurrentTurnIndex(prev => {
          const next = prev + 1;
          
          // Stop at end
          if (next >= replayData.turns.length) {
            setIsPlaying(false);
            return prev;
          }
          
          return next;
        });
      }, playbackSpeed);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isPlaying, playbackSpeed, replayData]);

  // Get current turn snapshot
  const getCurrentTurn = useCallback(() => {
    if (!replayData || currentTurnIndex >= replayData.turns.length) {
      return null;
    }
    
    return replayData.turns[currentTurnIndex];
  }, [replayData, currentTurnIndex]);

  // Get combat log for current turn
  const getCurrentCombat = useCallback(() => {
    if (!replayData) return [];
    
    const currentTurn = getCurrentTurn();
    if (!currentTurn) return [];
    
    return replayData.combatLog.filter(log => log.turn === currentTurn.turn);
  }, [replayData, getCurrentTurn]);

  // Control functions
  const play = useCallback(() => {
    if (currentTurnIndex >= (replayData?.turns.length || 0) - 1) {
      setCurrentTurnIndex(0); // Restart from beginning
    }
    setIsPlaying(true);
  }, [currentTurnIndex, replayData]);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const stepForward = useCallback(() => {
    setIsPlaying(false);
    setCurrentTurnIndex(prev => 
      Math.min(prev + 1, (replayData?.turns.length || 0) - 1)
    );
  }, [replayData]);

  const stepBackward = useCallback(() => {
    setIsPlaying(false);
    setCurrentTurnIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const jumpToTurn = useCallback((turnNumber) => {
    setIsPlaying(false);
    
    if (!replayData) return;
    
    const index = replayData.turns.findIndex(t => t.turn === turnNumber);
    
    if (index !== -1) {
      setCurrentTurnIndex(index);
    }
  }, [replayData]);

  const jumpToStart = useCallback(() => {
    setIsPlaying(false);
    setCurrentTurnIndex(0);
  }, []);

  const jumpToEnd = useCallback(() => {
    setIsPlaying(false);
    if (replayData) {
      setCurrentTurnIndex(replayData.turns.length - 1);
    }
  }, [replayData]);

  const setSpeed = useCallback((speed) => {
    setPlaybackSpeed(speed);
  }, []);

  return {
    // State
    replayData,
    currentTurnIndex,
    currentTurn: getCurrentTurn(),
    currentCombat: getCurrentCombat(),
    isPlaying,
    playbackSpeed,
    loading,
    error,
    
    // Computed
    totalTurns: replayData?.turns.length || 0,
    isAtStart: currentTurnIndex === 0,
    isAtEnd: currentTurnIndex >= (replayData?.turns.length || 0) - 1,
    
    // Controls
    play,
    pause,
    stepForward,
    stepBackward,
    jumpToTurn,
    jumpToStart,
    jumpToEnd,
    setSpeed,
    reload: loadReplay
  };
}
